import React, { type JSX, type ReactNode, useCallback } from "react";
import { Link } from "react-router-dom";

import styled, { type DefaultTheme, withTheme } from "styled-components";
import Icon, { type ICON_TYPE } from "components/icons/icon";

export enum ButtonColor {
  GREEN = "GREEN",
  BLUE = "BLUE",
  GRAY = "GRAY",
  LIGHTGRAY = "LIGHTGRAY",
}

function getButtonColor(color: ButtonColor, theme: DefaultTheme) {
  switch (color) {
    case ButtonColor.GREEN:
      return theme.colors.dsGreenMedium;

    case ButtonColor.GRAY:
      return theme.colors.brandGray50;

    case ButtonColor.LIGHTGRAY:
      return theme.colors.dsGrayAccent;

    default:
      return theme.colors.lspdGray800;
  }
}

interface IStyledButtonProps {
  color: string;
  padding?: "dense" | "normal";
  variant?: "contained" | "outline" | "text" | "accent";
}

const StyledButton = styled.button<IStyledButtonProps>`
  ${({ theme, variant = "contained" }) => {
    if (variant === "outline") {
      return `
          background: ${theme.colors.lspdSecondaryGradient};
          color: ${theme.colors.lspdGray800};
          border: 1px solid ${theme.colors.lspdSecondaryBorder};
        `;
    } else {
      return `
          background: ${theme.colors.lspdPrimaryGradient};
          color: ${theme.colors.brandWhite};
          border: 1px solid ${theme.colors.lspdPrimaryBorder};
        `;
    }
  }}
  font-family: ${({ theme }) => theme.text.mediumFamily};
  border-radius: ${(props) => props.theme.borderRadius};
  ${({ disabled }) => !disabled && `cursor: pointer`};
  font-size: ${(props) => props.theme.text.size.base};
  line-height: ${(props) => props.theme.text.size.large};
  padding: ${({ padding, theme: { spacing } }) => {
    return padding === "dense" ? "0" : `${spacing.small} ${spacing.medium}`;
  }};
  & * {
    vertical-align: middle;
  }
  &[disabled] {
    filter: saturate(0.5) opacity(0.7);
  }
  &:not([disabled]):hover {
    filter: opacity(0.9);
  }
`;

interface IStyledLabel {
  iconLeft: boolean;
  iconRight: boolean;
}

const StyledLabel = styled.span<IStyledLabel>`
  padding-left: ${(props) => (props.iconLeft ? props.theme.spacing.small : 0)};
  padding-right: ${(props) =>
    props.iconRight ? props.theme.spacing.small : 0};
`;

export interface IButtonProps extends Pick<IStyledButtonProps, "variant"> {
  form?: string;
  buttonColor?: ButtonColor;
  disabled?: boolean;
  excludeFromTabOrder?: boolean;
  iconLeft?: ICON_TYPE | JSX.Element;
  iconRight?: ICON_TYPE | JSX.Element;
  iconSize?: string;
  label: ReactNode;
  linkPath?: string;
  padding?: "dense" | "normal";
  theme: DefaultTheme;
  title?: string;
  type?: "button" | "submit" | "reset";
  onClick?: (event: React.MouseEvent) => void;
}

function Button({
  form,
  buttonColor = ButtonColor.BLUE,
  disabled = false,
  excludeFromTabOrder,
  iconLeft,
  iconRight,
  iconSize,
  label,
  linkPath = "",
  padding = "normal",
  theme,
  title,
  type,
  variant = "contained",
  onClick,
}: IButtonProps) {
  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      if (onClick) {
        if (!linkPath) {
          // We want to block behaviors like page refresh on form submission
          // but allow for navigation if the button is also a link
          event.preventDefault();
        }
        onClick(event);
      }
    },
    [onClick, linkPath]
  );

  const color = getButtonColor(buttonColor, theme);
  const iconProps = {
    color: variant === "contained" ? theme.colors.brandWhite : color,
    size: iconSize || theme.iconSize.xsmall,
  };
  // Links and buttons are tabbable by default, if excludeFromTabOrder, set -1
  const tabIndex = excludeFromTabOrder ? -1 : undefined;
  const button = (
    <StyledButton
      form={form}
      className="button"
      color={color}
      disabled={disabled}
      padding={padding}
      tabIndex={linkPath ? -1 : tabIndex}
      title={title}
      type={type}
      variant={variant}
      onClick={handleClick}>
      {iconLeft &&
        (React.isValidElement(iconLeft) ? (
          iconLeft
        ) : typeof iconLeft === "string" ? (
          <Icon type={iconLeft} {...iconProps} />
        ) : null)}
      {label && (
        <StyledLabel iconLeft={!!iconLeft} iconRight={!!iconRight}>
          {label}
        </StyledLabel>
      )}
      {iconRight &&
        (React.isValidElement(iconRight) ? (
          iconRight
        ) : typeof iconRight === "string" ? (
          <Icon type={iconRight} {...iconProps} />
        ) : null)}
    </StyledButton>
  );

  if (linkPath) {
    return (
      <Link
        style={{ color: "#fff" }}
        to={linkPath}
        tabIndex={tabIndex}
        title={title}>
        {button}
      </Link>
    );
  }
  return button;
}

export default withTheme(Button) as React.ComponentType<
  Omit<IButtonProps, "theme">
>;
